import React, { useState, useEffect } from "react";
import Template from "../../components/Template";
import { useNavigate } from "react-router-dom";
import FormLabel from "../../components/FormLabel";
import config from "../../config";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import api from "../../services/api";
import { maskCpfCnpj } from "../../utils/masks";
import usePermission from "../../hooks/usePermission";

const CadastrarCliente = ({ clienteId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(clienteId);
    const podeDeletar = usePermission("DELETAR_CLIENTE");

    // Estado único do formulário
    const [formData, setFormData] = useState({
        id: "",
        nome: "",
        cnpj: "",
    });

    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showAlertDialog, setShowAlertDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        console.log('Chamou o submit');
        e.preventDefault();
        setShowConfirmDialog(true);
        console.log('Encerrou o submit');
    };

    const handleDeleteRegister = async () => {
        try {
            await api.delete(`/clientes/${clienteId}`);
            setShowConfirmDeleteRegister(false);
            navigate("/clientes");
        } catch (error) {
            console.error("Erro ao remover cliente:", error);
            alert("Erro ao remover cliente.");
        }
    };

    const handleCnpjChange = (e) => {
        const value = e.target.value;
        // Aplica a máscara
        const maskedValue = maskCpfCnpj(value);
        setFormData((prev) => ({
            ...prev,
            cnpj: maskedValue,
        }));
    };

    useEffect(() => {
        if (isEditMode) {
            const fetchCliente = async () => {
                try {
                    const response = await api.get(`/clientes/${clienteId}`);
                    const data = response.data;

                    setFormData({
                        id: data.id,
                        nome: data.nome || "",
                        cnpj: data.cnpj || "",
                    });

                } catch (error) {
                    console.error("Erro ao carregar cliente:", error);
                    alert("Erro ao carregar o cliente. Tente novamente.");
                }
            };
            fetchCliente();
        }
    }, [isEditMode, clienteId]);

    const handleConfirm = async (e) => {
        setShowConfirmDialog(false);

        try {
            // Montamos um FormData apenas se você quiser enviar "multipart/form-data"
            const dataToSend = new FormData();

            dataToSend.append("nome", formData.nome);
            dataToSend.append("cnpj", formData.cnpj);

            if (isEditMode) {
                await api.put(`/clientes/${clienteId}`, dataToSend, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                await api.post(`/clientes`, dataToSend, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            setShowSuccessDialog(true);
            setTimeout(() => {
                setShowSuccessDialog(false);
                if (isEditMode) {
                    window.location.href = `/clientes/editar/${clienteId}`;
                } else {
                    window.location.href = `/clientes`;
                }
            }, 3000);
        } catch (error) {
            console.error(`Erro ao ${isEditMode ? "atualizar" : "cadastrar"} cliente:`, error);
            setAlertMessage(error.response?.data?.error || "Ocorreu um erro. Verifique os dados e tente novamente.");
            setShowAlertDialog(true);
        }
    };

    // Botão Voltar
    const handleBack = () => {
        navigate("/clientes");
    };

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        {isEditMode ? "Editar Cliente" : "Cadastrar Cliente"}
                    </h1>
                </div>

                {/* Informações Básicas */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium font-['Inter']">
                        Informações Básicas
                    </div>

                    {/* Linha 1 */}
                    <div className="flex flex-wrap gap-4">
                        {/* ID */}
                        <div className="w-[185px] flex flex-col gap-1.5">
                            <label className="text-slate-700 text-sm font-medium font-['Inter'] leading-tight">
                                ID
                            </label>
                            <input
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.id}
                                readOnly
                            />
                        </div>

                        {/* Nome do Cliente */}
                        <div className="w-[300px] grow flex flex-col gap-1.5">
                            <FormLabel htmlFor={'nome'}
                                isRequired={true}
                            >
                                Nome do Cliente
                            </FormLabel>
                            <input
                                name="nome"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.nome}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        {/* CNPJ */}
                        <div className="w-[374.75px] flex flex-col gap-1.5">
                            <FormLabel htmlFor={'cnpj'}
                                isRequired={false}
                            >
                                CNPJ
                            </FormLabel>
                            <input
                                name="cnpj"
                                type="text"
                                className="pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300"
                                value={formData.cnpj}
                                onChange={handleCnpjChange}
                                placeholder="00.000.000/0000-00"
                            />
                        </div>
                    </div>
                </div>

                {/* Botões de Ação */}
                <div className="flex items-center w-full gap-4">
                    {isEditMode && podeDeletar && (
                        <button
                            type="button"
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={() => setShowConfirmDeleteRegister(true)}
                        >
                            <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Apagar" />
                            <span>Apagar</span>
                        </button>
                    )}

                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>

                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                        >
                            <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                            <span>Salvar</span>
                        </button>
                    </div>
                </div>
            </form>
            {/** MODAIS de Confirmação / Sucesso */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                message="Deseja salvar as informações?"
            />

            <SuccessModal
                isVisible={showSuccessDialog}
                message="Informações salvas com sucesso!"
                onClose={() => setShowSuccessDialog(false)}
            />

            <ConfirmationModal
                isVisible={showConfirmDeleteRegister}
                onConfirm={handleDeleteRegister}
                onCancel={() => setShowConfirmDeleteRegister(false)}
                message="Deseja apagar este registro?"
                textButtonConfirm="Sim"
            />

            {/* Modal de Alerta de Erro */}
            {showAlertDialog && (
                <AlertDialog
                    isVisible={showAlertDialog}
                    message={alertMessage}
                    onClose={() => setShowAlertDialog(false)}
                />
            )}

        </Template>
    );
};

export default CadastrarCliente;

